import React, { useState, useEffect } from 'react';
import { useConfirm } from "context/ConfirmContext";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import CircularProgress from "@mui/material/CircularProgress";
import { apiRequest } from "services/tenantUserAdminService";
import { TC } from "../tokens/text";

/*Bearer ${token}`
 * HMRCOAuthConnect
 *
 * Props:
 *   tenantId   {string}
 *   clientId   {string}
 *   onConnected {function}  — called after successful connection
 */
function HMRCOAuthConnect({ tenantId, clientId, onConnected }) {
  const [status, setStatus]       = useState(null);   // null | { connected, expiresAt, isExpired }
  const [loading, setLoading]     = useState(false);
  const [connecting, setConnecting] = useState(false);
  const confirm = useConfirm();
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError]         = useState(null);

  const loadStatus = async () => {
    if (!tenantId || !clientId) return;
    setLoading(true);
    try {
      const data = await apiRequest(
        `/hmrc/auth/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(clientId)}/status`
      );
      setStatus(data);
    } catch (e) {
      setStatus({ connected: false });
    } finally { setLoading(false); }
  };

  useEffect(() => { loadStatus(); }, [tenantId, clientId]); // eslint-disable-line

  // Handle ?hmrc_connected=1 redirect from OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("hmrc_connected") === "1") {
      window.history.replaceState({}, "", window.location.pathname);
      loadStatus().then(() => onConnected && onConnected());
    }
    if (params.get("hmrc_error")) {
      setError(decodeURIComponent(params.get("hmrc_error")));
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []); // eslint-disable-line

  const handleConnect = async () => {
    setConnecting(true); setError(null);
    try {
      const returnUrl = window.location.href;
      const data = await apiRequest(
        `/hmrc/auth/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(clientId)}/connect?returnUrl=${encodeURIComponent(returnUrl)}`
      );
      // Redirect browser to HMRC OAuth authorization page
      window.location.href = data.authUrl;
    } catch (e) {
      setError(e.message || "Failed to start HMRC connection");
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    const ok = await confirm({
      title: "Disconnect HMRC",
      message: "Disconnect this client from HMRC?\nYou will need to re-authorise to submit returns.",
      confirmLabel: "Disconnect",
      variant: "warning",
    });
    if (!ok) return;
    setDisconnecting(true); setError(null);
    try {
      await apiRequest(
        `/hmrc/auth/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(clientId)}/disconnect`,
        { method: "DELETE" }
      );
      setStatus({ connected: false });
    } catch (e) {
      setError(e.message);
    } finally { setDisconnecting(false); }
  };

  if (loading) {
    return <VuiBox display="flex" alignItems="center" gap={1}><CircularProgress size={16} sx={{ color: TC.secondary }} /><VuiTypography sx={{ color: TC.secondary, fontSize: 13 }}>Checking HMRC connection…</VuiTypography></VuiBox>;
  }

  return (
    <VuiBox role="region" aria-label="HMRC connection status">
      {error && (
        <VuiBox mb={1.5} sx={{ background: "rgba(255,71,87,0.1)", border: "1px solid rgba(255,71,87,0.3)", borderRadius: 2, p: "8px 12px" }} role="alert">
          <VuiTypography sx={{ color: "#f44336", fontSize: 12 }}>{error}</VuiTypography>
        </VuiBox>
      )}

      {status?.connected && !status?.isExpired ? (
        <VuiBox>
          <VuiBox display="flex" alignItems="center" gap={1.5} mb={1.5} sx={{ background: "rgba(1,181,116,0.08)", border: "1px solid rgba(1,181,116,0.25)", borderRadius: 2, p: "10px 12px" }} role="status" aria-live="polite">
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#01B574", display: "inline-block" }} aria-hidden="true" />
            <VuiTypography sx={{ color: "#01B574", fontSize: 13, fontWeight: 600 }}>Connected to HMRC</VuiTypography>
            {status.expiresAt && (
              <VuiTypography sx={{ color: TC.secondary, fontSize: 11, ml: "auto" }}>
                Token expires {new Date(status.expiresAt).toLocaleString("en-GB")}
              </VuiTypography>
            )}
          </VuiBox>
          <VuiButton size="small" color="error" variant="outlined" onClick={handleDisconnect} disabled={disconnecting} sx={{ fontSize: 11 }} aria-label="Disconnect from HMRC">
            {disconnecting ? "Disconnecting…" : "Disconnect HMRC"}
          </VuiButton>
        </VuiBox>
      ) : (
        <VuiBox>
          {status?.connected && status?.isExpired && (
            <VuiBox mb={1.5} sx={{ background: "rgba(247,183,49,0.08)", border: "1px solid rgba(247,183,49,0.2)", borderRadius: 2, p: "8px 12px" }} role="alert">
              <VuiTypography sx={{ color: "#F7B731", fontSize: 12 }}>⚠️ HMRC token expired — please reconnect.</VuiTypography>
            </VuiBox>
          )}
          <VuiButton color="info" variant="contained" onClick={handleConnect} disabled={connecting} sx={{ fontSize: 12, fontWeight: 700 }} aria-label="Connect to HMRC Making Tax Digital">
            {connecting ? "Redirecting to HMRC…" : "🔗 Connect to HMRC MTD"}
          </VuiButton>
          <VuiTypography sx={{ color: TC.secondary, fontSize: 11, mt: 1 }}>
            Authorises this software to submit VAT returns on behalf of the client via HMRC Making Tax Digital.
          </VuiTypography>
        </VuiBox>
      )}
    </VuiBox>
  );
}

export default HMRCOAuthConnect;




